import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getStores } from '../api/api';
import { BarChart3, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Building2, Calendar, Download, RefreshCw, Minus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ComparativeReports = () => {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('monthly'); // monthly, stores
  const [monthlyData, setMonthlyData] = useState(null);
  const [storeComparisonData, setStoreComparisonData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (activeTab === 'monthly') {
      loadMonthlyComparison();
    } else {
      loadStoreComparison();
    }
  }, [activeTab, selectedYear, selectedMonth]);

  const loadStores = async () => {
    try {
      const data = await getStores();
      setStores(data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMonthlyComparison = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/monthly-comparison?year=${selectedYear}&month=${selectedMonth}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMonthlyData(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreComparison = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/store-ranking?year=${selectedYear}&month=${selectedMonth}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStoreComparisonData(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'Q') => {
    return `${currency}${(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    
    if (activeTab === 'monthly' && monthlyData) {
      const wsData = [
        ['Reporte Comparativo Mensual'],
        [`Período: ${monthlyData.current_month_name} ${selectedYear} vs ${monthlyData.previous_month_name}`],
        [],
        ['Métrica', 'Mes Actual', 'Mes Anterior', 'Diferencia', '% Cambio'],
        ['Ventas', monthlyData.current.sales, monthlyData.previous.sales, monthlyData.current.sales - monthlyData.previous.sales, calculateChange(monthlyData.current.sales, monthlyData.previous.sales) + '%'],
        ['Gastos', monthlyData.current.expenses, monthlyData.previous.expenses, monthlyData.current.expenses - monthlyData.previous.expenses, calculateChange(monthlyData.current.expenses, monthlyData.previous.expenses) + '%'],
        ['Balance', monthlyData.current.net, monthlyData.previous.net, monthlyData.current.net - monthlyData.previous.net, calculateChange(monthlyData.current.net, monthlyData.previous.net) + '%'],
        ['Transacciones', monthlyData.current.transactions, monthlyData.previous.transactions, monthlyData.current.transactions - monthlyData.previous.transactions, calculateChange(monthlyData.current.transactions, monthlyData.previous.transactions) + '%']
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, 'Comparativo Mensual');
    }
    
    if (activeTab === 'stores' && storeComparisonData) {
      const wsData = [
        ['Ranking de Tiendas'],
        [`Período: ${storeComparisonData.month_name} ${selectedYear}`],
        [],
        ['Posición', 'Tienda', 'Ventas', 'Gastos', 'Balance', '% del Total'],
        ...storeComparisonData.stores.map((s, i) => [
          i + 1, s.name, s.sales, s.expenses, s.net, s.percentage + '%'
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, 'Ranking Tiendas');
    }
    
    XLSX.writeFile(wb, `Reporte_Comparativo_${selectedYear}_${selectedMonth}.xlsx`);
  };

  const months = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (!['admin', 'manager', 'supervisor'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No tienes permisos para ver esta sección</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <BarChart3 className="text-blue-600" size={28} />
            <span>Reportes Comparativos</span>
          </h1>
          <p className="text-gray-600 mt-1">Análisis de rendimiento y comparativas</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            data-testid="month-selector"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select
            data-testid="year-selector"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            data-testid="export-excel-btn"
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <Download size={18} />
            <span>Excel</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md p-1 inline-flex">
        <button
          data-testid="tab-monthly"
          onClick={() => setActiveTab('monthly')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'monthly' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Calendar size={16} className="inline mr-2" />
          Mes vs Mes Anterior
        </button>
        <button
          data-testid="tab-stores"
          onClick={() => setActiveTab('stores')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'stores' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Building2 size={16} className="inline mr-2" />
          Comparativa Tiendas
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-blue-600" size={32} />
        </div>
      ) : activeTab === 'monthly' && monthlyData ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: 'Ventas', current: monthlyData.current.sales, previous: monthlyData.previous.sales, colorClass: 'text-emerald-600', borderColor: '#10b981', icon: TrendingUp },
              { label: 'Gastos', current: monthlyData.current.expenses, previous: monthlyData.previous.expenses, colorClass: 'text-red-600', borderColor: '#ef4444', icon: TrendingDown, invertColor: true },
              { label: 'Balance', current: monthlyData.current.net, previous: monthlyData.previous.net, colorClass: 'text-blue-600', borderColor: '#3b82f6', icon: BarChart3 },
              { label: 'Transacciones', current: monthlyData.current.transactions, previous: monthlyData.previous.transactions, colorClass: 'text-purple-600', borderColor: '#9333ea', icon: RefreshCw, isCurrency: false }
            ].map((item, idx) => {
              const change = parseFloat(calculateChange(item.current, item.previous));
              const isNeutral = item.current === 0 && item.previous === 0;
              const isPositive = item.invertColor ? change < 0 : change > 0;
              return (
                <div key={idx} data-testid={`metric-card-${item.label.toLowerCase()}`} className="bg-white rounded-xl shadow-md p-5 border-l-4" style={{ borderLeftColor: item.borderColor }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">{item.label}</span>
                    <item.icon size={20} className={item.colorClass} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {item.isCurrency !== false ? formatCurrency(item.current) : item.current.toLocaleString()}
                  </p>
                  <div className="flex items-center mt-2">
                    {isNeutral ? (
                      <Minus size={16} className="text-gray-400" />
                    ) : isPositive ? (
                      <ArrowUpRight size={16} className="text-emerald-600" />
                    ) : (
                      <ArrowDownRight size={16} className="text-red-600" />
                    )}
                    <span className={`text-sm font-semibold ${isNeutral ? 'text-gray-400' : isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                      {isNeutral ? '—' : `${Math.abs(change)}%`}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">vs mes anterior</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Chart */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {monthlyData.current_month_name} vs {monthlyData.previous_month_name}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[
                  { name: 'Ventas', actual: monthlyData.current.sales, anterior: monthlyData.previous.sales },
                  { name: 'Gastos', actual: monthlyData.current.expenses, anterior: monthlyData.previous.expenses },
                  { name: 'Balance', actual: monthlyData.current.net, anterior: monthlyData.previous.net }
                ]}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ borderRadius: '8px' }} />
                <Legend />
                <Bar dataKey="actual" name={monthlyData.current_month_name} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="anterior" name={monthlyData.previous_month_name} fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : activeTab === 'stores' && storeComparisonData ? (
        <div className="space-y-6">
          {/* Ranking Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-slate-700 to-blue-700">
              <h3 className="text-lg font-semibold text-white">
                Ranking de Tiendas - {storeComparisonData.month_name} {selectedYear}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">#</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase">Tienda</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase">Ventas</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase">Gastos</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase">Balance</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase">% Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {storeComparisonData.stores.map((store, idx) => (
                    <tr key={store.id} className={`${idx < 3 ? 'bg-emerald-50' : ''} hover:bg-blue-50`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">
                        {store.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-emerald-600 font-medium">
                        {formatCurrency(store.sales, store.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-red-600 font-medium">
                        {formatCurrency(store.expenses, store.currency)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-right font-bold ${store.net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(store.net, store.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                          {store.percentage}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-800 text-white">
                  <tr>
                    <td className="px-6 py-3 font-bold" colSpan={2}>TOTAL</td>
                    <td className="px-6 py-3 text-right font-bold">{formatCurrency(storeComparisonData.totals.sales)}</td>
                    <td className="px-6 py-3 text-right font-bold">{formatCurrency(storeComparisonData.totals.expenses)}</td>
                    <td className="px-6 py-3 text-right font-bold">{formatCurrency(storeComparisonData.totals.net)}</td>
                    <td className="px-6 py-3 text-right font-bold">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Ventas por Tienda</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={storeComparisonData.stores.map(s => ({
                  name: s.name.length > 15 ? s.name.substring(0, 15) + '...' : s.name,
                  ventas: s.sales,
                  gastos: s.expenses,
                  balance: s.net
                }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ borderRadius: '8px' }} />
                <Legend />
                <Bar dataKey="ventas" name="Ventas" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="gastos" name="Gastos" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600">No hay datos disponibles para el período seleccionado</p>
        </div>
      )}
    </div>
  );
};

export default ComparativeReports;
